import { type ThunkAction } from "redux-thunk";
import { type AnyAction } from "~/app/store/types";
import { type PriceInfo } from "~/domains/pricing/api/PriceInfo";
import { type ProductData, ProductType } from "~/domains/product/types/ProductData";
import { type AvailabilityInfo } from "~/domains/product/api/pna/AvailabilityInfo";
import { productsActions } from "~/domains/product/redux/pnaReducers";
import type { RootState } from "~/app/store";
import { getLinearPriceInfo } from "~/domains/linear/api/GetLinearPriceInfoSingle";
import { getCacheKey, selectProductData, selectProductDescriptor } from "~/domains/product/redux/selectors";
import { fetchProductInfo } from "~/domains/product/api/fetchProductInfo";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { getHybrisProductCode } from "~/utils/cartHelper";
import {
  invalidateAvailabilityRequestCache,
  requestAvailabilityDataSingle,
} from "~/domains/product/api/availabilityService";
import { invalidatePriceRequestCache, requestPriceDataSingle } from "~/domains/pricing/api/priceService";
import { getLinearAvailabilityInfo } from "~/domains/linear/api/GetLinearAvailabilityInfoSingle";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { ADD_PRODUCT_BASIC, addBaseProductDataAction } from "~/domains/product/redux/basicProductActions";
import {
  currentPnaRequestGeneration,
  invalidatePnaRequestGeneration,
  isCurrentPnaRequestGeneration,
} from "~/domains/product/redux/pnaRequestGeneration";

export { ADD_PRODUCT_BASIC, addBaseProductDataAction };

type ThunkResult<R> = ThunkAction<R, RootState, undefined, AnyAction>;

// Availability calls in progress
const aPromisesInFlight: { [code: string]: Promise<AvailabilityInfo> } = {};
// Price calls in progress
const pPromisesInFlight: { [code: string]: Promise<PriceInfo[]> } = {};

// ─── Throttled flush queue for bulk PnA results ───────────────────────────────
// Individual API results are enqueued as they arrive. Every FLUSH_THROTTLE_MS the
// queue flushes a single bulkFinish dispatch (one Redux update / one re-render)
// and then resolves the corresponding deferred promises so that
// orchestratePnARefreshRegular sees populated state via getState().
const FLUSH_THROTTLE_MS = 1000;
const ACCOUNT_CONTEXT_REQUEST_ABORTED = "Price and Availability request superseded by account context change";

interface PnAFlushEntry<T> {
  cacheKey: string;
  result: { ok: true; data: T } | { ok: false };
  deferred: {
    resolve: (d: T) => void;
    reject: (e: unknown) => void;
    promise: Promise<T>;
  };
  promise: Promise<T>;
  generation: number;
}

function createPnAFlushQueue<T>(
  bulkFinish: (data: Record<string, T>, errorKeys: string[]) => AnyAction,
  promisesInFlight: Record<string, Promise<T>>,
) {
  let pending: PnAFlushEntry<T>[] = [];
  let timer: ReturnType<typeof setTimeout> | null = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let dispatchRef: ((...args: any[]) => any) | null = null;

  function flush() {
    timer = null;
    const batch = pending;
    const dispatch = dispatchRef;
    pending = [];
    if (!dispatch || batch.length === 0) {
      return;
    }

    const currentBatch = batch.filter((entry) => isCurrentPnaRequestGeneration(entry.generation));
    const successData: Record<string, T> = {};
    const errorKeys: string[] = [];
    for (const { cacheKey, result } of currentBatch) {
      if (result.ok) {
        successData[cacheKey] = result.data;
      } else {
        errorKeys.push(cacheKey);
      }
    }

    // Single batch Redux update for all results that arrived in this throttle window.
    if (currentBatch.length > 0) {
      dispatch(bulkFinish(successData, errorKeys));
    }

    // Resolve/reject deferreds AFTER dispatch so getState() returns populated data
    // when orchestratePnARefreshRegular continues after its awaited promise.
    for (const { cacheKey, result, deferred, promise, generation } of batch) {
      if (!isCurrentPnaRequestGeneration(generation)) {
        if (promisesInFlight[cacheKey] === promise) {
          delete promisesInFlight[cacheKey];
        }
        deferred.reject(new Error("Price and Availability request superseded by account context change"));
        continue;
      }
      if (promisesInFlight[cacheKey] === promise) {
        delete promisesInFlight[cacheKey];
      }
      if (result.ok) {
        deferred.resolve(result.data);
      } else {
        deferred.reject(new Error("fetch failed"));
      }
    }
  }

  return {
    enqueue(
      entry: PnAFlushEntry<T>,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch: (...args: any[]) => any,
    ) {
      dispatchRef = dispatch;
      pending.push(entry);
      if (timer === null) {
        timer = setTimeout(flush, FLUSH_THROTTLE_MS);
      }
    },
    invalidate(reason: Error) {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }

      const staleEntries = pending;
      pending = [];
      dispatchRef = null;
      staleEntries.forEach(({ cacheKey, deferred, promise }) => {
        if (promisesInFlight[cacheKey] === promise) {
          delete promisesInFlight[cacheKey];
        }
        deferred.reject(reason);
      });
    },
  };
}

const priceFlushQueue = createPnAFlushQueue<PriceInfo[]>(productsActions.price.bulkFinish, pPromisesInFlight);

const availabilityFlushQueue = createPnAFlushQueue<AvailabilityInfo>(
  productsActions.availability.bulkFinish,
  aPromisesInFlight,
);

/**
 * Detaches raw Price and Availability requests from their legacy caches at the account boundary.
 * Network promises cannot be aborted by these legacy services, so their callbacks are also fenced
 * by the generation check and queued bulk results are rejected synchronously here.
 */
export function invalidatePnaRequests(): void {
  invalidatePnaRequestGeneration();
  const reason = new Error(ACCOUNT_CONTEXT_REQUEST_ABORTED);
  invalidatePriceRequestCache();
  invalidateAvailabilityRequestCache();
  priceFlushQueue.invalidate(reason);
  availabilityFlushQueue.invalidate(reason);
  Object.keys(pPromisesInFlight).forEach((cacheKey) => delete pPromisesInFlight[cacheKey]);
  Object.keys(aPromisesInFlight).forEach((cacheKey) => delete aPromisesInFlight[cacheKey]);
}

/**
 *
 * @param productOrCode - code of the product to load
 * @param priceReference -
 * @param retryIfError - try loading again if the previous attempt failed
 */
export function requestPriceData(
  productOrCode: ProductData | string,
  priceReference: string,
  retryIfError?: boolean,
  packCode?: string,
  forceRefresh?: boolean,
  activeCartId?: string,
): ThunkResult<Promise<PriceInfo[]>> {
  return (dispatch, getState) => {
    const state = getState();
    const product = typeof productOrCode === "string" ? selectProductData(productOrCode)(state) : productOrCode;
    const cacheKey = getCacheKey({ productCode: product.code, priceReference: priceReference });

    const entry = state.products.price[cacheKey];

    if (!userHasPermission(Permission.PRICE)) {
      if (!entry) {
        const data: PriceInfo[] = [];
        dispatch(productsActions.price.success(cacheKey, []));
        return Promise.resolve(data);
      }
      return Promise.resolve(entry.data!);
    }

    if (entry && (!forceRefresh || entry.state === "loading")) {
      const promiseInFlight: Promise<PriceInfo[]> | undefined = pPromisesInFlight[cacheKey];
      if (entry.data) {
        return Promise.resolve(entry.data);
      } else if (entry.state === "error" && !retryIfError) {
        return Promise.reject(new Error("error"));
      } else if (entry.state === "loading" || promiseInFlight) {
        return promiseInFlight;
      }
    }

    const promise = dispatch(fetchPriceAction(product, priceReference, packCode, activeCartId));
    pPromisesInFlight[cacheKey] = promise;
    return promise;
  };
}

function fetchPriceAction(
  product: ProductData,
  priceReference: string,
  packCode?: string,
  activeCartId?: string,
): ThunkResult<Promise<PriceInfo[]>> {
  return async (dispatch) => {
    const requestGeneration = currentPnaRequestGeneration();
    // We use pnaCode to fetch, but it should be cached under code - so that the fake product-bundle can find the data
    const cacheKey = getCacheKey({ productCode: product.code, priceReference: priceReference });

    dispatch(productsActions.price.load(cacheKey));

    let sourcePromise: Promise<PriceInfo[]>;
    if (product.type === ProductType.CONFIGURATOR) {
      sourcePromise = getLinearPriceInfo(
        getHybrisProductCode(product.code),
        product.configId,
        priceReference,
        activeCartId,
      );
    } else {
      sourcePromise = requestPriceDataSingle({
        productCode: product.pnaCode ?? product.code,
        priceReference,
        packCode,
      });
    }

    const promise = sourcePromise.then(
      (info) => {
        if (!isCurrentPnaRequestGeneration(requestGeneration)) {
          throw new Error(ACCOUNT_CONTEXT_REQUEST_ABORTED);
        }
        dispatch(productsActions.price.success(cacheKey, info));
        return info;
      },
      (error: unknown) => {
        if (isCurrentPnaRequestGeneration(requestGeneration)) {
          dispatch(productsActions.price.error(cacheKey));
        }
        throw error;
      },
    );

    promise
      .finally(() => {
        if (pPromisesInFlight[cacheKey] === promise) {
          delete pPromisesInFlight[cacheKey];
        }
      })
      .catch(() => undefined);

    return promise;
  };
}

/**
 *
 * @param productOrCode - code of the product to load
 * @param retryIfError - try loading again if the previous attempt failed
 */
export function requestAvailabilityData(
  productOrCode: ProductData | string,
  retryIfError?: boolean,
  packCode?: string,
  forceRefresh?: boolean,
  activeCartId?: string,
): ThunkResult<Promise<AvailabilityInfo | undefined>> {
  return (dispatch, getState) => {
    const state = getState();
    const product = typeof productOrCode === "string" ? selectProductData(productOrCode)(state) : productOrCode;
    const cacheKey = getCacheKey({ productCode: product.code, priceReference: "" });

    const entry = getState().products.availability[cacheKey];

    if (!userHasPermission(Permission.AVAILABILITY)) {
      if (!entry) {
        dispatch(productsActions.availability.success(cacheKey, undefined));
        return Promise.resolve(undefined);
      }
      return Promise.resolve(entry.data!);
    }

    if (entry && (!forceRefresh || entry.state === "loading")) {
      const promiseInFlight: Promise<AvailabilityInfo> | undefined = aPromisesInFlight[cacheKey];
      if (entry.data) {
        return Promise.resolve(entry.data);
      } else if (entry.state === "error" && !retryIfError) {
        return Promise.reject(new Error("error"));
      } else if (entry.state === "loading" || promiseInFlight) {
        return promiseInFlight;
      }
    }

    const promise = dispatch(fetchAvailabilityAction(product, packCode, activeCartId));
    aPromisesInFlight[cacheKey] = promise;
    return promise;
  };
}

function fetchAvailabilityAction(
  product: ProductData,
  packCode?: string,
  activeCartId?: string,
): ThunkResult<Promise<AvailabilityInfo>> {
  return (dispatch) => {
    const requestGeneration = currentPnaRequestGeneration();
    // We use pnaCode to fetch, but it should be cached under code - so that the fake product-bundle can find the data
    const cacheKey = getCacheKey({ productCode: product.code, priceReference: "" });

    dispatch(productsActions.availability.load(cacheKey));

    let sourcePromise: Promise<AvailabilityInfo>;
    if (product.type === ProductType.CONFIGURATOR) {
      sourcePromise = getLinearAvailabilityInfo(product.pnaCode, product.configId, activeCartId);
    } else {
      sourcePromise = requestAvailabilityDataSingle({ productCode: product.pnaCode ?? product.code, packCode });
    }

    const promise = sourcePromise.then(
      (info) => {
        if (!isCurrentPnaRequestGeneration(requestGeneration)) {
          throw new Error(ACCOUNT_CONTEXT_REQUEST_ABORTED);
        }
        dispatch(productsActions.availability.success(cacheKey, info));
        return info;
      },
      (error: unknown) => {
        if (isCurrentPnaRequestGeneration(requestGeneration)) {
          dispatch(productsActions.availability.error(cacheKey));
        }
        throw error;
      },
    );

    promise
      .finally(() => {
        if (aPromisesInFlight[cacheKey] === promise) {
          delete aPromisesInFlight[cacheKey];
        }
      })
      .catch(() => undefined);
    return promise;
  };
}

interface RequestFullProductDataOptions {
  forceFetchBasicData?: boolean;
  fetchPrice?: boolean;
  fetchAvailability?: boolean;
}

export function requestFullProductData(
  code: string,
  priceReference: string,
  options: RequestFullProductDataOptions = {},
): ThunkResult<Promise<ProductDescriptor>> {
  return async (dispatch, getState) => {
    const state = getState();

    priceReference = priceReference === "-" ? "" : priceReference;

    let product = state.products.basic[code];
    if (!product || (options.forceFetchBasicData && !product?.isFullProduct)) {
      product = await fetchProductInfo(code);
      dispatch(addBaseProductDataAction(product, options.forceFetchBasicData));
    }

    const fetchPrice = options.fetchPrice ?? true;
    const fetchAvailability = options.fetchAvailability ?? true;

    const pricePromise = fetchPrice ? dispatch(requestPriceData(code, priceReference, true)) : Promise.resolve([]);
    const availabilityPromise = fetchAvailability
      ? dispatch(requestAvailabilityData(code, true))
      : Promise.resolve(undefined);

    await Promise.all([pricePromise, availabilityPromise]);

    return selectProductDescriptor(getState(), { productCode: code, priceReference });
  };
}

export function requestBaseProductData(
  code: string,
  options: RequestFullProductDataOptions = {},
): ThunkResult<Promise<ProductData>> {
  return async (dispatch) => {
    const product = await fetchProductInfo(code);
    dispatch(addBaseProductDataAction(product, options.forceFetchBasicData));

    return product;
  };
}

export interface BulkPriceRequest {
  productCode: string;
  priceReference: string;
  packCode?: string;
  activeCartId?: string;
}

export function requestBulkPriceData(items: BulkPriceRequest[]): ThunkResult<void> {
  return (dispatch, getState) => {
    const requestGeneration = currentPnaRequestGeneration();
    if (!userHasPermission(Permission.PRICE)) {
      return;
    }

    const state = getState();
    const uncached = items.filter((item) => {
      const cacheKey = getCacheKey(item);
      const entry = state.products.price[cacheKey];
      return !entry?.data;
    });

    if (uncached.length === 0) {
      return;
    }

    const seen = new Set<string>();
    const deduped = uncached.filter((item) => {
      const key = getCacheKey(item);
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });

    // Resolve canonical cache keys upfront (product.code may differ from item.productCode)
    const resolvedItems = deduped.map((item) => {
      const product = selectProductData(item.productCode)(getState());
      const cacheKey = getCacheKey({
        productCode: product?.code ?? item.productCode,
        priceReference: item.priceReference,
      });
      return { item, product, cacheKey };
    });

    const cacheKeys = resolvedItems.map((r) => r.cacheKey);
    dispatch(productsActions.price.bulkLoad(cacheKeys));

    // Register per-key deferred promises in pPromisesInFlight so that requestPriceData
    // can attach to in-flight bulk fetches instead of triggering duplicate network requests.
    const deferreds = new Map<
      string,
      {
        resolve: (data: PriceInfo[]) => void;
        reject: (reason: unknown) => void;
        promise: Promise<PriceInfo[]>;
      }
    >();
    for (const cacheKey of cacheKeys) {
      let resolve!: (data: PriceInfo[]) => void;
      let reject!: (reason: unknown) => void;
      const promise = new Promise<PriceInfo[]>((res, rej) => {
        resolve = res;
        reject = rej;
      });
      pPromisesInFlight[cacheKey] = promise;
      deferreds.set(cacheKey, { resolve, reject, promise });
    }

    // Fire individual fetches. Each result feeds into the throttled flush queue,
    // which batches Redux updates every 160ms to avoid per-product re-renders.
    for (const { item, product, cacheKey } of resolvedItems) {
      const deferred = deferreds.get(cacheKey)!;

      (product?.type === ProductType.CONFIGURATOR
        ? getLinearPriceInfo(
            getHybrisProductCode(product.code),
            product.configId,
            item.priceReference,
            item.activeCartId,
          )
        : requestPriceDataSingle({
            productCode: product?.pnaCode ?? item.productCode,
            priceReference: item.priceReference,
            packCode: item.packCode,
          })
      ).then(
        (data) =>
          priceFlushQueue.enqueue(
            {
              cacheKey,
              result: { ok: true, data },
              deferred,
              promise: deferred.promise,
              generation: requestGeneration,
            },
            dispatch,
          ),
        () =>
          priceFlushQueue.enqueue(
            { cacheKey, result: { ok: false }, deferred, promise: deferred.promise, generation: requestGeneration },
            dispatch,
          ),
      );
    }
  };
}

export interface BulkAvailabilityRequest {
  productCode: string;
  packCode?: string;
  activeCartId?: string;
}

export function requestBulkAvailabilityData(items: BulkAvailabilityRequest[]): ThunkResult<void> {
  return (dispatch, getState) => {
    const requestGeneration = currentPnaRequestGeneration();
    if (!userHasPermission(Permission.AVAILABILITY)) {
      return;
    }

    const state = getState();
    const uncached = items.filter((item) => {
      const cacheKey = getCacheKey({ productCode: item.productCode });
      const entry = state.products.availability[cacheKey];
      return !entry?.data;
    });

    if (uncached.length === 0) {
      return;
    }

    const seen = new Set<string>();
    const deduped = uncached.filter((item) => {
      if (seen.has(item.productCode)) {
        return false;
      }
      seen.add(item.productCode);
      return true;
    });

    // Resolve canonical cache keys upfront (product.code may differ from item.productCode)
    const resolvedItems = deduped.map((item) => {
      const product = selectProductData(item.productCode)(getState());
      const cacheKey = getCacheKey({ productCode: product?.code ?? item.productCode, priceReference: "" });
      return { item, product, cacheKey };
    });

    const cacheKeys = resolvedItems.map((r) => r.cacheKey);
    dispatch(productsActions.availability.bulkLoad(cacheKeys));

    // Register per-key deferred promises in aPromisesInFlight so that requestAvailabilityData
    // can attach to in-flight bulk fetches instead of triggering duplicate network requests.
    const deferreds = new Map<
      string,
      {
        resolve: (data: AvailabilityInfo) => void;
        reject: (reason: unknown) => void;
        promise: Promise<AvailabilityInfo>;
      }
    >();
    for (const cacheKey of cacheKeys) {
      let resolve!: (data: AvailabilityInfo) => void;
      let reject!: (reason: unknown) => void;
      const promise = new Promise<AvailabilityInfo>((res, rej) => {
        resolve = res;
        reject = rej;
      });
      aPromisesInFlight[cacheKey] = promise;
      deferreds.set(cacheKey, { resolve, reject, promise });
    }

    // Fire individual fetches. Each result feeds into the throttled flush queue,
    // which batches Redux updates every 160ms to avoid per-product re-renders.
    for (const { item, product, cacheKey } of resolvedItems) {
      const deferred = deferreds.get(cacheKey)!;

      (product?.type === ProductType.CONFIGURATOR
        ? getLinearAvailabilityInfo(product.pnaCode, product.configId, item.activeCartId)
        : requestAvailabilityDataSingle({
            productCode: product?.pnaCode ?? item.productCode,
            packCode: item.packCode,
          })
      ).then(
        (data) =>
          availabilityFlushQueue.enqueue(
            {
              cacheKey,
              result: { ok: true, data },
              deferred,
              promise: deferred.promise,
              generation: requestGeneration,
            },
            dispatch,
          ),
        () =>
          availabilityFlushQueue.enqueue(
            { cacheKey, result: { ok: false }, deferred, promise: deferred.promise, generation: requestGeneration },
            dispatch,
          ),
      );
    }
  };
}
